import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiPatch } from '@/features/shared/utils/dataFetching'
import { type CustomerBaseResponse } from '@/features/account/services/User/types'
import {
  type FetchShoppingStoresParams,
  type SelectStoreParams,
} from '@/features/shop/services/ShoppingStore/types'
import { useUserId } from '@/features/account/services/User/hooks'
import { DELIVERY_TYPES } from '@/features/shared/constants/global'
import { UserQuery } from '@/features/account/services/User/constants'
import { useSegwayRouter } from '@/features/shared/hooks/useSegwayRouter'
import { useMutationUpdateAddress } from '@/features/account/services/Addresses/mutations'
import { getAddresses } from '@/features/account/services/Addresses/utils'
import { giftOrderMetadataAtom } from '@/features/purchase/state/GiftOrder/atoms'
import { useSetShopSessionFlag } from '@/features/shop/state/ShopSessionFlags/atoms'
import { useClearActiveOrder } from '@/features/shop/state/SelectedActiveOrder/hooks'
import { useSetAtom } from 'jotai'
import { RESET } from 'jotai/utils'
import { logError } from '@/features/shared/utils/logger'
import { shoppingStoresQueryOptions } from '@/features/shop/services/ShoppingStore/queries'
import { usePrefetchCmsShopQueries } from '@/features/cms/services/CMS/hooks'
import { userQueryOptions } from '@/features/account/services/User/queryOptions'
import { datadogRum } from '@datadog/browser-rum'

export const SelectStoreMutationKey = 'Select store'

/**
 *
 * Note: If using `mutateAsync` to select a store, please set `skipPrefetch` to `true` due to the
 * following technical limitations... When we send store_id and new address_id to update a user's
 * address, this process is in flight before the user's current address data can be updated.
 * This results in `prefetchCMSRetailerQueries` running twice, once with the old address and once
 * with the new address, leading to multiple unnecessary network calls.
 */

export const useMutationSelectStore = () => {
  const queryClient = useQueryClient()
  const userId = useUserId() ?? 0
  const router = useSegwayRouter()
  const clearActiveOrder = useClearActiveOrder()
  const { mutateAsync: updateAddress } = useMutationUpdateAddress()
  const setShopSessionFlag = useSetShopSessionFlag()
  const setGiftOrderMetadata = useSetAtom(giftOrderMetadataAtom)
  const { prefetchCMSRetailerQueries } = usePrefetchCmsShopQueries()

  return useMutation({
    mutationKey: [SelectStoreMutationKey],
    mutationFn: (params: SelectStoreParams) => {
      const { id: store_id, metro_id, store_location_id, name } = params.store
      const payload = {
        store_id,
        default_shopping_address_id: params.addressId,
        order_delivery_type: params.isPickup ? DELIVERY_TYPES.PICKUP : DELIVERY_TYPES.STANDARD,
      }

      if (metro_id && store_id && store_location_id && !params.skipPrefetch) {
        prefetchCMSRetailerQueries({
          metro_id,
          store_id,
          store_location_id,
          retailerName: name,
        })
      }
      return apiPatch<CustomerBaseResponse>({
        config: { url: `api/v1/customers/${userId}`, data: payload },
        fetcherName: 'useMutationSelectStore',
      })
    },
    onSuccess: async (response, params) => {
      const { shouldClearCurrentActiveOrder = true } = params

      if (shouldClearCurrentActiveOrder) {
        clearActiveOrder()
      }

      const storeLocationMismatch =
        !!params.store.store_location_id &&
        response.store_location_id !== params.store.store_location_id
      if (storeLocationMismatch) {
        logError('store location mismatch', {
          cachedStoreLocation: params.store.store_location_id,
          responseStoreLocation: response.store_location_id,
        })
      }
      datadogRum.addAction('changeStore', {
        storeLocationMismatch,
        clientStoreLocation: params.store.store_location_id,
        responseStoreLocation: response.store_location_id,
      })

      setShopSessionFlag('isCartOOSExpanded', true)
      if (!params.isGuestSignup) {
        setGiftOrderMetadata(RESET)
      }

      if (params.isPickup) {
        const addresses = getAddresses(queryClient)
        const address = {
          ...addresses?.find((add) => add.id === params.addressId),
          store_location_id: response.store_location_id,
          pickup_store_location_id: params.store.store_location_id,
        }
        await updateAddress(address)
      }

      if (params.route) {
        queryClient.setQueryData(userQueryOptions.queryKey, (prevUser) =>
          prevUser
            ? {
                ...prevUser,
                default_shopping_address_id: response.default_shopping_address_id,
                metro: response.metro,
                metro_id: response.metro_id,
                store: response.store,
                store_id: response.store_id,
                store_location_id: response.store_location_id,
              }
            : undefined,
        )
        router.push(params.route)
      }
      return queryClient.invalidateQueries({ queryKey: [UserQuery] })
    },
  })
}

export const useMutationFetchShoppingStores = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (query: FetchShoppingStoresParams) => {
      const address = 'address' in query && query.address
      const addressId = 'address_id' in query && query.address_id
      const params = {
        ...(addressId && { address_id: addressId }),
        ...(address && {
          ...('street1' in address && {
            address_line_1: address.street1,
            address_city: address.city,
            address_state: address.state,
          }),
          address_zip_code: address.zip_code,
        }),
      }
      return queryClient.query(shoppingStoresQueryOptions(params))
    },
  })
}
